import _Button from "@mui/material/Button";
import _FormControlLabel from "@mui/material/FormControlLabel";
import _Box from "@mui/material/Box";
import _FormControl from "@mui/material/FormControl";
import _Typography from "@mui/material/Typography";
import _Checkbox from "@mui/material/Checkbox";
import _TextField from "@mui/material/TextField";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { red } from '@mui/material/colors';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import * as Yup from 'yup';
import { prodFrontendUrl } from '../../environment';
import { AuthService } from '../../services';
import { isValidPassword } from './password-validation';
function SignUpForm({
  widgetConfig,
  onRegistered,
  ...props
}) {
  const {
    i18n: {
      language
    },
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    isSubmitting,
    values,
    touched,
    errors,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: {
      email: '',
      password: '',
      passwordConfirmation: '',
      approveGCU: false
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().required(t('commons.sign_in_form.email_required') || ''),
      password: Yup.string().required(t('commons.sign_up_form.password_pattern') || '').test('password-pattern', t('commons.sign_up_form.password_pattern') || '', password => Boolean(password && isValidPassword(password))),
      passwordConfirmation: Yup.string().when('password', {
        is: password => password,
        then: shema => shema.oneOf([Yup.ref('password')], t('commons.sign_up_form.passwords_must_match') || '').required(t('commons.sign_up_form.passwords_must_match') || '')
      }),
      approveGCU: Yup.boolean().oneOf([true])
    }),
    enableReinitialize: true,
    onSubmit
  });
  async function onSubmit({
    email,
    password
  }, {
    setSubmitting,
    setErrors
  }) {
    setSubmitting(true);
    try {
      const user = await AuthService.register(email, password, language);
      onRegistered(user);
    } catch (err) {
      if (err && typeof err === 'object' && err.code === 409) {
        setErrors({
          email: t('commons.sign_up_form.email_already_exists') || ''
        });
      } else {
        enqueueSnackbar(t('commons.sign_up_form.server_error'));
      }
      setSubmitting(false);
    }
  }
  return /*#__PURE__*/React.createElement(StyledForm, _extends({
    onSubmit: handleSubmit
  }, props), /*#__PURE__*/React.createElement(_TextField, {
    disabled: isSubmitting,
    error: Boolean(errors.email && touched.email),
    helperText: touched.email && errors.email,
    id: "email",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_in_form.email"
    }),
    margin: "dense",
    name: "email",
    onChange: handleChange,
    size: "small",
    type: "email",
    value: values.email,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_TextField, {
    autoComplete: "new-password",
    disabled: isSubmitting,
    error: Boolean(errors.password && touched.password),
    helperText: touched.password && errors.password || /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_up_form.password_pattern"
    }),
    id: "password",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_in_form.password"
    }),
    margin: "dense",
    name: "password",
    onChange: handleChange,
    size: "small",
    type: "password",
    value: values.password,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_TextField, {
    autoComplete: "new-password",
    disabled: isSubmitting,
    error: Boolean(errors.passwordConfirmation && touched.passwordConfirmation),
    helperText: touched.passwordConfirmation && errors.passwordConfirmation,
    id: "passwordConfirmation",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.sign_up_form.password_confirmation"
    }),
    margin: "dense",
    name: "passwordConfirmation",
    onChange: handleChange,
    size: "small",
    type: "password",
    value: values.passwordConfirmation,
    variant: "outlined"
  }), /*#__PURE__*/React.createElement(_FormControl, {
    component: "fieldset",
    disabled: isSubmitting,
    margin: "dense"
  }, /*#__PURE__*/React.createElement(StyledFormControlLabel, {
    control: /*#__PURE__*/React.createElement(_Checkbox, {
      color: "primary",
      name: "approveGCU",
      onChange: handleChange,
      style: errors.approveGCU && touched.approveGCU ? {
        color: red[500]
      } : {},
      value: values.approveGCU
    }),
    label: /*#__PURE__*/React.createElement(_Typography, {
      variant: "caption"
    }, /*#__PURE__*/React.createElement(Trans, {
      components: [/*#__PURE__*/React.createElement(_Typography, {
        color: "primary",
        component: "a",
        href: `${prodFrontendUrl}/fr/eula/`,
        key: 0,
        variant: "caption"
      }), /*#__PURE__*/React.createElement(_Typography, {
        color: "primary",
        component: "a",
        href: `${prodFrontendUrl}/fr/privacy-policy/`,
        key: 0,
        variant: "caption"
      })],
      i18nKey: "commons.sign_up_form.accept_gcu_and_privacy_policy"
    }))
  })), widgetConfig?.code === 'lav' && /*#__PURE__*/React.createElement(_Box, {
    sx: {
      marginTop: '8px'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    variant: "caption"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: [/*#__PURE__*/React.createElement(_Typography, {
      color: "primary",
      component: "a",
      href: "https://www.loireavelo.fr/inscription-newsletter/",
      key: 0,
      target: "_blank",
      variant: "caption"
    })],
    i18nKey: "commons.sign_up_form.share_data_with_lav"
  }))), /*#__PURE__*/React.createElement(StyledButton, {
    color: "primary",
    disabled: isSubmitting,
    size: "medium",
    type: "submit",
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.sign_up"
  })));
}
const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`;
const StyledFormControlLabel = styled(_FormControlLabel)`
  && {
    span {
      line-height: 1.1;
    }
  }
`;
const StyledButton = styled(_Button)`
  && {
    margin-top: 8px;
  }
`;
export default SignUpForm;